/**
 * /case-studies/on-device-ai-testing
 *
 * Source: ai-testing-use-cases.md, use case 2.
 *
 * ONE SOURCE ONLY. Page 1 had both the markdown and the live page, and the
 * markdown turned out to be a heavy compression — every element checked from
 * the original was missing from it. No screenshots were supplied for this page,
 * so it is built from the rewrite alone and is very likely thinner than the
 * live original. Worth diffing against the real page before this ships.
 *
 * TWO GAPS, neither rendered as a placeholder — the client asked for those to
 * come off. Both are recorded here so they are not lost:
 *
 * 1. CLIENT SHAPE. The source asks for: what kind of app, roughly how many
 *    engineers, which platforms. No name needed.
 * 2. RESULTS FIGURES. "Reduced manual QA effort per model update" is the claim.
 *    By how much? What did validating a model update cost before, and after?
 *
 * The source is explicit that the offline guarantee is the strongest claim and
 * should lead the results. It does.
 */

import type { IconName } from "../../components/ui/waq/Icon";

export const seo = {
  title: "On-Device AI Testing — Validating Offline AI with Zero Data Egress",
  description:
    "Testing AI that runs on the user's device. Two-layer pipeline: mobile UI automation captures responses, a local judge scores them. No data leaves the device.",
} as const;

export const hero = {
  breadcrumb: { label: "Case Studies", href: "/case-studies" },
  badge: "Use Case #2",
  heading: "Testing On-Device AI in a Privacy-First Mobile Application",
  body: "Our client's app runs its AI models entirely on the user's device. No cloud, no uploads, all processing local on mobile-optimised models.",
  /** The question the whole page answers. */
  questionLead: "Which creates a testing problem with no standard answer:",
  question:
    "how do you validate output quality when there is no server to intercept, no API to call, and no data permitted to leave the device?",
  /** Not rendered. The source flags this as needed. */
  shapeGap:
    "Client shape — no name, but what kind of app, roughly how many engineers, which platforms?",
} as const;

export type Constraint = { title: string; body: string; icon: IconName };

export const constraints = {
  heading: "How do you test AI that runs on-device with no cloud?",
  display: "The constraint that makes this hard",
  intro: "Four constraints, all simultaneous:",
  items: [
    {
      title: "No server-side visibility",
      body: "Responses exist only inside the app",
      icon: "search",
    },
    {
      title: "Hardware variability",
      body: "The same model behaves differently across devices",
      icon: "cube",
    },
    {
      title: "Compressed models",
      body: "Quantised models don't behave like their full-size versions",
      icon: "layers",
    },
    {
      title: "Privacy constraints",
      body: "Test data cannot leave the device",
      icon: "shieldCheck",
    },
  ] as Constraint[],
} as const;

/**
 * The two-layer pipeline. The whole point is that both layers run inside the
 * device boundary, so the diagram draws the boundary rather than a flow.
 */
export const approach = {
  display: "Our approach",
  lead: "A two-layer pipeline, both layers running locally.",
  boundaryLabel: "On the device",
  layers: [
    {
      step: "Layer 1",
      title: "Mobile UI automation",
      points: [
        "Realistic multi-turn conversation scenarios driven through the app itself",
        "Voice and text input exercised",
        "Fully offline operation confirmed by asserting no network calls occur",
        "Actual AI responses captured for evaluation",
      ],
      icon: "code",
    },
    {
      step: "Layer 2",
      title: "AI quality evaluation",
      points: [
        "Captured conversation logs become scored test cases",
        "A local judge evaluates accuracy, coherence, relevance, and safety",
        "Structured pass/fail against defined thresholds",
        "No external service is contacted",
      ],
      icon: "brain",
    },
  ],
  egress: {
    label: "Nothing leaves",
    body: "No external service is contacted. Test data never leaves the device.",
  },
  altText:
    "Both layers of the pipeline run inside the device boundary. Layer one drives the app through realistic multi-turn scenarios and captures the actual AI responses. Layer two scores those captured responses with a local judge against defined thresholds. No external service is contacted at any point, so no test data leaves the device.",
} as const;

export const together = {
  display: "Why the two layers matter together",
  alone: [
    {
      title: "UI automation alone",
      body: "Tells you the app works.",
      icon: "code",
    },
    {
      title: "AI evaluation alone",
      body: "Needs responses it has no way to obtain.",
      icon: "brain",
    },
  ] as Constraint[],
  joined:
    "Joined, one automated run catches both UI regressions and AI quality regressions — and every response scored is a real response from a real device, not a simulation.",
  trackedLabel: "Tracked across releases",
  tracked: [
    "AI accuracy per update",
    "Response quality trends",
    "Regressions against the previous version",
  ],
} as const;

export const results = {
  display: "Results",
  /** Not rendered. The record of what is outstanding. */
  gap: "Real figures. 'Reduced manual QA effort per model update' — by how much? What did validating a model update cost before, and after?",
  /**
   * The source: "The third bullet is the strongest and should lead. For a
   * privacy-first product, a testing approach that preserves the privacy
   * guarantee isn't a convenience — it's the only approach that doesn't
   * compromise the thing being sold." So it leads, and it is the marked one.
   */
  leadClaim: {
    title: "Fully offline — zero data leaves the device",
    body: "For a privacy-first product, a testing approach that preserves the privacy guarantee isn't a convenience. It's the only approach that doesn't compromise the thing being sold.",
  },
  claimsLabel: "Also claimed, pending figures",
  claims: [
    "Quality evaluation grounded in real app responses rather than simulated ones",
    "UI and AI regressions caught in a single pipeline",
    "Reduced manual QA effort per model update",
  ],
  /**
   * Scope, not outcomes. Read off the copy above: two layers, the four
   * dimensions the local judge scores, and the zero-egress guarantee that the
   * title tag itself states. NO FIGURE HERE WAS INVENTED.
   */
  scopeLabel: "What the pipeline guarantees",
  scope: [
    {
      value: "2",
      label: "Layers, both local",
      note: "Mobile UI automation captures real responses; a local judge scores them",
    },
    {
      value: "4",
      label: "Dimensions scored on-device",
      note: "Accuracy, coherence, relevance, safety",
    },
    {
      value: "0",
      label: "Data egress",
      note: "No external service contacted; test data never leaves the device",
    },
  ],
} as const;

export const applies = {
  display: "Where this applies",
  items: [
    "On-device AI assistants",
    "Privacy-sensitive applications in healthcare, finance and legal",
    "Offline-first mobile AI",
    "Apps running compressed or quantised models",
  ],
} as const;

export const related = {
  display: "Related",
  items: [
    {
      label: "End-to-End Testing of AI Chatbots at Scale",
      href: "/case-studies/ai-chatbot-testing",
    },
    {
      label: "AI-Powered Test Generation for Enterprise Test Automation",
      href: "/case-studies/ai-test-generation",
    },
    { label: "AI-Driven Validation", href: "/services/ai-driven-validation" },
  ],
} as const;
